import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box, Card, CardContent, Grid, Typography, Stack, Alert, AlertTitle,
  Chip, Switch, FormControlLabel, TextField, Button,
  Divider, LinearProgress, Paper, Tooltip, alpha, CircularProgress,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Link } from 'react-router-dom';
import apiService, { authFetch } from '../../services/apiServices';
import SelectController from '../../controllers/SelectController';
import InputController from '../../controllers/InputController';
import Toast from '../actions/Toasts';
import globals from '../../services/globals';
import usePermissions from '../../hooks/usePermissions';
import { getAuthToken } from '../../services/authSession';
import { leaveTypeChoices, leaveTypeColor, MATERNITY_LEAVE_TYPE_ID, splitForMaternity } from '../../config/leaveTypes';

// Nga `config/leaveTypes.js` — i njëjti katalog si tabela `leave_types`.
// Lista e vjetër e koduar këtu i jepte numra të tjerë të njëjtave lloje.
const HOLIDAY_TYPES = leaveTypeChoices();


function workingDaysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  let count = 0, d = new Date(s);
  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function BalanceBar({ label, used, allowance, color }) {
  const isUnlimited = !isFinite(allowance);
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / allowance) * 100));
  const remaining = isUnlimited ? '∞' : Math.max(0, allowance - used);
  const barColor = pct >= 90 ? 'error' : pct >= 65 ? 'warning' : 'success';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.4}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: 11 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Chip
            size="small"
            label={`${used} prd`}
            sx={{ fontSize: 9, height: 16, bgcolor: `${color}15`, color }}
          />
          <Chip
            size="small"
            label={isUnlimited ? '∞' : `${remaining} mbtn`}
            sx={{ fontSize: 9, height: 16 }}
            color={isUnlimited ? 'default' : pct >= 90 ? 'error' : 'success'}
          />
        </Stack>
      </Stack>
      {!isUnlimited && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={barColor}
          sx={{ height: 4, borderRadius: 2 }}
        />
      )}
    </Box>
  );
}

export default function AddHoliday() {
  const { hasAnyRole } = usePermissions();
  const isEmployee = hasAnyRole(['employee']) && !hasAnyRole(['super_admin', 'company_admin', 'hr_manager', 'team_lead', 'payroll_manager']);
  // Everyone can choose the leave type; employees default to annual but may change it.
  const allowedTypes = HOLIDAY_TYPES;

  const [employees, setEmployees] = useState([]);
  const [lockWorker, setLockWorker] = useState(false); // true once the logged-in employee is locked to themselves
  const [empHolidays, setEmpHolidays] = useState([]);
  const [daysPlanned, setDaysPlanned] = useState(0);
  const [overlapWarning, setOverlapWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', variant: '' });
  const [halfDay, setHalfDay] = useState(null); // null=full day, 'first'=paradite, 'second'=pasdite
  const [leaveHours, setLeaveHours] = useState(''); // '' = not using hours mode; number string = partial hours
  const [attachFile, setAttachFile] = useState(null);     // File object for sick-leave attachment
  const [attachUploading, setAttachUploading] = useState(false);
  const [overBalance, setOverBalance] = useState(null);

  const [POLICY, setPOLICY] = useState({
    1: { name: 'Vjetor',         allowance: 20 },
    6: { name: 'Me pagesë',      allowance: 5 },
    4: { name: 'Pa pagesë',      allowance: Infinity },
    2: { name: 'Mjekësor',       allowance: 20 },
    3: { name: 'Martesor',       allowance: 5 },
    5: { name: 'Jashtëzakonshëm', allowance: 5 },
    7: { name: 'Dhënie gjaku',   allowance: 1 },
  });

  const {
    register, handleSubmit, control, setValue, watch,
    formState: { errors },
  } = useForm();

  const startDate       = watch('StartDate');
  const endDate         = watch('EndDate');
  const selectedEmployee = watch('Employee');
  const holidayTypeVal   = watch('HolidayType');

  useEffect(() => {
    apiService().get('selectworker').then(r => {
      if (!r?.success) return;
      const list = r.data || [];
      setEmployees(list);
      // Default the worker to the logged-in user. worker_id lives either at the top
      // level of the session (SignIn) or inside .data (loginService) — check both.
      const me = globals().getLogedInUser() || {};
      const d = me.data || {};
      const myWorkerId = String(me.worker_id ?? me.WorkerId ?? d.worker_id ?? d.WorkerId ?? '');
      if (myWorkerId) {
        const mine = list.find(w => String(w.Id ?? w.id) === myWorkerId);
        if (mine) {
          setValue('Employee', mine);
          // Only lock the field once we actually matched — never leave an employee
          // stuck with an empty, disabled worker field they can't fill.
          if (isEmployee) setLockWorker(true);
        }
      }
    });
    globals().getSettings().then(s => {
      if (!s) return;
      setPOLICY({
        1: { name: 'Vjetor',         allowance: Number(s.AnnualLeaveAllowance)     || 20 },
        6: { name: 'Me pagesë',      allowance: Number(s.PaidLeaveAllowance)       || 5 },
        4: { name: 'Pa pagesë',      allowance: Number(s.UnpaidLeaveAllowance) > 0 ? Number(s.UnpaidLeaveAllowance) : Infinity },
        2: { name: 'Mjekësor',       allowance: Number(s.MedicalLeaveAllowance)    || 20 },
        3: { name: 'Martesor',       allowance: Number(s.MaritalLeaveAllowance)    || 5 },
        5: { name: 'Jashtëzakonshëm', allowance: Number(s.ExceptionalLeaveAllowance) || 5 },
        7: { name: 'Dhënie gjaku',   allowance: Number(s.BloodDonationLeaveAllowance) || 1 },
      });
    });
    // Employees: pre-select annual leave and lock type
    if (isEmployee) setValue('HolidayType', { id: 1, name: 'Vjetor' });
  }, [isEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Lehonia është e punonjëses, ndaj lista ngushtohet kur zgjidhet ai lloj.
   *
   * Serveri e refuzon gjithsesi një kërkesë lehonie për një burrë
   * (`backend/services/maternityEligibility.js`); kjo është që njeriu ta shohë
   * rregullin te lista, jo ta mësojë nga një gabim pas dërgimit.
   *
   * Kush e ka gjininë e paplotësuar te profili nuk shfaqet — numri i tyre
   * thuhet poshtë fushës, që mungesa e dikujt të lexohet si fushë e pambushur
   * dhe jo si person që s'ekziston.
   */
  const maternitySelected = Number(holidayTypeVal?.id ?? holidayTypeVal) === MATERNITY_LEAVE_TYPE_ID;
  const { employeeOptions, hiddenNoGender } = useMemo(() => {
    if (!maternitySelected) return { employeeOptions: employees, hiddenNoGender: 0 };
    const { eligible, hiddenUnknown } = splitForMaternity(employees);
    return { employeeOptions: eligible, hiddenNoGender: hiddenUnknown };
  }, [employees, maternitySelected]);

  // Një punëtor i zgjedhur më parë që s'i përket më listës duhet të bjerë:
  // përndryshe fusha do të tregonte një emër që opsionet nuk e përmbajnë, dhe
  // kërkesa do të dërgohej për t'u refuzuar nga serveri.
  useEffect(() => {
    if (!maternitySelected || !selectedEmployee) return;
    const id = String(selectedEmployee.Id ?? selectedEmployee.id ?? '');
    if (!employeeOptions.some((w) => String(w.Id ?? w.id) === id)) setValue('Employee', null);
  }, [maternitySelected, employeeOptions, selectedEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEmployee?.Id) { setEmpHolidays([]); return; }
    apiService().query('getEmployeesHolidays', 'id=' + selectedEmployee.Id)
      .then(r => { if (r?.success) setEmpHolidays(r.data || []); });
  }, [selectedEmployee]);

  useEffect(() => {
    if (halfDay) {
      setDaysPlanned(0.5);
      if (startDate) setValue('EndDate', startDate);
    } else if (leaveHours) {
      const lh = parseFloat(leaveHours);
      setDaysPlanned(isNaN(lh) ? 0 : +(lh / 8).toFixed(3));
      if (startDate) setValue('EndDate', startDate);
    } else {
      setDaysPlanned(workingDaysBetween(startDate, endDate));
    }
    if (!selectedEmployee || !startDate) { setOverlapWarning(''); return; }
    const effectEnd = (halfDay || leaveHours) ? startDate : endDate;
    if (!effectEnd) { setOverlapWarning(''); return; }
    const s = new Date(startDate), e = new Date(effectEnd);
    const overlap = (empHolidays || []).some(h => s <= new Date(h.EndDate) && e >= new Date(h.StartDate));
    setOverlapWarning(overlap ? 'Datat përputhen me një pushim ekzistues për këtë punëtor!' : '');
  }, [startDate, endDate, selectedEmployee, empHolidays, halfDay, leaveHours]);

  // Reset half-day and partial-hours when leave type changes away from annual
  useEffect(() => {
    if (holidayTypeVal?.id !== 1) { setHalfDay(null); setLeaveHours(''); }
  }, [holidayTypeVal]);

  const currentYearUsage = () => {
    const year = new Date().getFullYear();
    const byType = {};
    (empHolidays || []).forEach(h => {
      const sd = new Date(h.StartDate);
      if (sd.getFullYear() !== year) return;
      const days = h.LeaveHours != null ? h.LeaveHours / 8 : (h.HalfDay ? 0.5 : Number(h.holidayDays || workingDaysBetween(h.StartDate, h.EndDate)));
      byType[h.HolidayType] = (byType[h.HolidayType] || 0) + (isNaN(days) ? 0 : days);
    });
    return byType;
  };

  const getBalance = () => {
    const t = holidayTypeVal?.id;
    const usage = currentYearUsage();
    const pol = POLICY[t];
    const used = t ? (usage[t] || 0) : 0;
    const remain = pol ? (isFinite(pol.allowance) ? Math.max(0, pol.allowance - used) : Infinity) : 0;
    return { t, pol, used, remain, planned: daysPlanned || 0, exceedBy: daysPlanned - remain };
  };

  const onSubmit = async (data) => {
    // The SERVER decides whether an overage blocks: settings.leave_overbalance_mode
    // is 'warn' or 'block'. The old local check blocked unconditionally, which
    // contradicted a 'warn' configuration and hid the real balance from the user.
    const plannedDays = leaveHours ? parseFloat(leaveHours) / 8 : (halfDay ? 0.5 : daysPlanned);
    try {
      const qs = new URLSearchParams({
        workerId: String(data.Employee?.Id ?? ''),
        leaveTypeId: String(data.HolidayType?.id ?? ''),
        startDate: String(data.StartDate ?? '').slice(0, 10),
        endDate: String(data.EndDate ?? '').slice(0, 10),
        days: String(plannedDays ?? ''),
      });
      const vr = await authFetch(`/api/holidays/balance-check?${qs}`);
      const vj = await vr.json().catch(() => ({}));
      const v = vj?.data;
      // Periudhë e bllokuar strikte (politika sipas projektit): serveri e refuzon
      // gjithsesi te POST; këtu ndalet para dërgimit me të njëjtin mesazh.
      const strictBlackout = (v?.blackouts || []).find((b) => b.strict);
      if (strictBlackout) {
        setToast({ open: true, variant: 'error', message: `Periudhë e bllokuar «${strictBlackout.name}» (${strictBlackout.from} – ${strictBlackout.to}): pushimi nuk lejohet në këto data.` });
        return;
      }
      if (v?.exceeds) {
        setOverBalance(v);
        if (v.blocked) {
          setToast({ open: true, variant: 'error',
            message: `${v.message} Kërkuar: ${v.requestedDays} ditë · Në dispozicion: ${v.availableDays} · Tejkalim: ${v.excessDays}.` });
          return;                       // block mode — refuse
        }
        // warn mode — the banner stays visible and the request goes through.
      } else {
        setOverBalance(null);
      }
    } catch { /* the server re-checks on POST; never block on a probe failure */ }

    setSaving(true);
    const payload = {
      ...data,
      Approve: data.Approve || false,
      Employee: data.Employee.Id,
      HolidayType: data.HolidayType.id,
      HalfDay: halfDay || null,
      LeaveHours: leaveHours ? parseFloat(leaveHours) : null,
      HolidayDays: leaveHours ? parseFloat(leaveHours) / 8 : (halfDay ? 0.5 : daysPlanned),
      CreatedBy: '',
    };
    apiService().post('holidays', payload).then(async r => {
      setSaving(false);
      if (r?.success) {
        const newId = r.data?.id || r.data?.Id;
        // Upload attachment when sick leave and file selected
        if (attachFile && newId) {
          setAttachUploading(true);
          try {
            const token = getAuthToken();
            const fd = new FormData();
            fd.append('attachment', attachFile);
            const apiBase = process.env.REACT_APP_API_URL || '';
            await fetch(`${apiBase}/api/holidays/${newId}/upload-attachment`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
          } catch {} finally { setAttachUploading(false); }
        }
        setToast({ open: true, variant: 'success', message: 'Kërkesa u ruajt me sukses!' });
        setTimeout(() => { window.location.href = `#/holidays/editholiday/${r.data.id}`; }, 900);
      } else {
        setToast({ open: true, variant: 'error', message: 'Ndodhi një gabim gjatë ruajtjes.' });
      }
    });
  };

  const bal = getBalance();
  const typeColor = leaveTypeColor(holidayTypeVal?.id) || '#64748B';
  const exceedsLimit = bal.t && bal.pol && isFinite(bal.remain) && bal.planned > bal.remain;

  return (
    <Box sx={{ width: '100%' }}>
      <Toast {...toast} />

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <EventAvailableIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                Kërkesë për Pushim
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Plotëso të dhënat dhe dërgo kërkesën
              </Typography>
            </Box>
          </Stack>
        {overBalance?.exceeds && (
          <Alert severity={overBalance.blocked ? 'error' : 'warning'} sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {overBalance.blocked ? 'Kërkesa nuk mund të dërgohet' : 'Tejkalim i balancës'}
            </AlertTitle>
            Ditë të kërkuara: <b>{overBalance.requestedDays}</b> · Në dispozicion:{' '}
            <b>{overBalance.availableDays}</b> · Tejkalim: <b>{overBalance.excessDays}</b>
            {!overBalance.blocked && ' — kërkesa mund të dërgohet, por mbikëqyrësi njoftohet.'}
          </Alert>
        )}

          <Stack direction="row" spacing={0.75}>
            <Button
              component={Link}
              to="/my-leaves"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ borderRadius: 1.5, py: 0.5 }}
            >
              Kthehu
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit(onSubmit)}
              disabled={saving}
              size="small"
              sx={{ borderRadius: 1.5, py: 0.5, bgcolor: '#3E8FA3', '&:hover': { bgcolor: '#256878' } }}
            >
              {saving ? 'Duke ruajtur…' : 'Ruaj kërkesën'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {/* ── Form card ── */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {/* Colored top strip based on leave type */}
            <Box sx={{ height: 4, bgcolor: typeColor, borderRadius: '8px 8px 0 0' }} />
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 2 } }}>

              {/* Section: Punëtori & lloji */}
              <Stack direction="row" spacing={0.75} alignItems="center" mb={1.25}>
                <WorkHistoryIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                  Punëtori & Lloji i Pushimit
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <SelectController
                    placeholder="Punëtori"
                    error={errors.Employee}
                    control={control}
                    options={employeeOptions}
                    name="Employee"
                    register={{ ...register('Employee') }}
                    disabled={lockWorker}
                  />
                  {maternitySelected && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                      {hiddenNoGender > 0
                        ? `Vetëm punonjëset. ${hiddenNoGender} punonjës nuk shfaqen sepse gjinia s’është plotësuar te profili.`
                        : 'Vetëm punonjëset.'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectController
                    placeholder="Lloji i pushimit"
                    error={errors.HolidayType}
                    control={control}
                    options={allowedTypes}
                    name="HolidayType"
                    register={{ ...register('HolidayType') }}
                  />
                </Grid>
              </Grid>

              {/* Half-day / Partial-hour options — annual leave only */}
              {holidayTypeVal?.id === 1 && (
                <Box sx={{ mt: 1.25, p: 1, bgcolor: '#F0F9FF', borderRadius: 1.5, border: '1px solid #BAE6FD' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={0.75}>
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={halfDay !== null}
                          onChange={e => { setLeaveHours(''); setHalfDay(e.target.checked ? 'first' : null); }}
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>½ Ditë</Typography>}
                    />
                    {halfDay !== null && (
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          variant={halfDay === 'first' ? 'contained' : 'outlined'}
                          onClick={() => setHalfDay('first')}
                          sx={{ py: 0.25, px: 1.5, fontSize: '0.72rem', borderRadius: 1.5 }}
                        >
                          Paradite
                        </Button>
                        <Button
                          size="small"
                          variant={halfDay === 'second' ? 'contained' : 'outlined'}
                          onClick={() => setHalfDay('second')}
                          sx={{ py: 0.25, px: 1.5, fontSize: '0.72rem', borderRadius: 1.5 }}
                        >
                          Pasdite
                        </Button>
                      </Stack>
                    )}
                    {halfDay !== null && (
                      <Typography variant="caption" color="text.secondary">
                        Vetëm data e fillimit — data e mbarimit vendoset automatikisht
                      </Typography>
                    )}
                    {halfDay === null && (
                      <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">Orë:</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={leaveHours}
                            onChange={e => {
                              const v = e.target.value;
                              setLeaveHours(v);
                              if (v && parseFloat(v) > 0) setHalfDay(null);
                            }}
                            onBlur={e => {
                              const v = parseFloat(e.target.value);
                              if (!isNaN(v)) setLeaveHours(String(Math.min(7.5, Math.max(0.5, Math.round(v * 2) / 2))));
                            }}
                            inputProps={{ min: 0.5, max: 7.5, step: 0.5 }}
                            placeholder="0"
                            sx={{ width: 80, '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                          />
                          {leaveHours && (
                            <Typography variant="caption" color="text.secondary">
                              ≈ {(parseFloat(leaveHours) / 8).toFixed(2)} ditë
                            </Typography>
                          )}
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Section: Datat */}
              <Stack direction="row" spacing={0.75} alignItems="center" mb={1.25}>
                <CalendarMonthIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                  Periudha e Pushimit
                </Typography>
              </Stack>

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={(halfDay || leaveHours) ? 12 : 5}>
                  <InputController
                    placeholder="Data e fillimit"
                    error={errors.StartDate}
                    control={control}
                    type="date"
                    register={{ ...register('StartDate') }}
                  />
                </Grid>
                {!halfDay && !leaveHours && (
                  <>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.disabled" fontWeight={500}>deri</Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <InputController
                        placeholder="Data e mbarimit"
                        error={errors.EndDate}
                        control={control}
                        type="date"
                        register={{ ...register('EndDate') }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Days summary */}
              {(daysPlanned > 0 || leaveHours) && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.5, p: 1, borderRadius: 1.5,
                    bgcolor: exceedsLimit ? 'error.50' : '#f0fdf4',
                    border: '1px solid',
                    borderColor: exceedsLimit ? 'error.200' : '#bbf7d0',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                  }}
                >
                  {exceedsLimit
                    ? <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    : <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                  }
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={700} color={exceedsLimit ? 'error.main' : '#15803d'}>
                      {leaveHours
                        ? `${leaveHours}h (${(parseFloat(leaveHours) / 8).toFixed(2)}d)`
                        : halfDay ? '½ ditë pune e planifikuar' : `${daysPlanned} ditë pune të planifikuara`}
                    </Typography>
                    {bal.pol && (
                      <Typography variant="caption" color="text.secondary">
                        {isFinite(bal.remain)
                          ? exceedsLimit
                            ? `Tejkalon me ${bal.exceedBy} ditë (${bal.remain} disponueshme)`
                            : `Do të mbeten ${bal.remain - bal.planned} ditë nga "${bal.pol.name}"`
                          : `Pushim pa kufi ditor — "${bal.pol.name}"`
                        }
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={leaveHours ? `${leaveHours}h` : halfDay ? '½d' : `${daysPlanned}d`}
                    size="small"
                    sx={{
                      fontWeight: 800, fontSize: 13, height: 28, minWidth: 42,
                      bgcolor: exceedsLimit ? 'error.main' : '#3E8FA3',
                      color: '#fff',
                    }}
                  />
                </Paper>
              )}

              {overlapWarning && (
                <Alert severity="warning" sx={{ mt: 1, borderRadius: 1.5, py: 0.5 }}>
                  {overlapWarning}
                </Alert>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Attachment for sick leave */}
              {holidayTypeVal?.id === 2 && (
                <Box sx={{ p: 1.5, bgcolor: '#FFF7F0', borderRadius: 1.5, border: '1px solid #FDBA74', mb: 1.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" mb={1}>
                    <AttachFileIcon sx={{ fontSize: 15, color: '#EA580C' }} />
                    <Typography variant="caption" fontWeight={700} color="#EA580C" textTransform="uppercase" letterSpacing={0.5}>
                      Dëshmi mjekësore
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.75}>
                    <Button component="label" size="small" variant="outlined"
                      startIcon={attachUploading ? <CircularProgress size={12} color="inherit" /> : <UploadFileIcon sx={{ fontSize: 14 }} />}
                      disabled={attachUploading}
                      sx={{ fontSize: '0.72rem', borderRadius: 1.5, color: '#EA580C', borderColor: '#FDBA74' }}
                    >
                      {attachFile ? attachFile.name : 'Zgjidh dokumentin (PDF, JPG, PNG)'}
                      <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                        onChange={e => {
                          const f = e.target.files?.[0] || null;
                          if (f && f.size > 5 * 1024 * 1024) {
                            setToast({ open: true, variant: 'error', message: 'Skedari nuk duhet të kalojë 5 MB.' });
                            return;
                          }
                          setAttachFile(f);
                        }}
                      />
                    </Button>
                    {attachFile && (
                      <Typography variant="caption" color="text.secondary">
                        {(attachFile.size / 1024).toFixed(0)} KB
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    Opcionale · max 5 MB · PDF, JPG, PNG
                  </Typography>
                </Box>
              )}

              {/* Aprovimi & Komenti */}
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Switch
                        name="Approve"
                        size="small"
                        onChange={e => setValue('Approve', e.target.checked)}
                        checked={watch('Approve') || false}
                        color="success"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Aprovuar
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Komenti / Arsyeja"
                    placeholder="Shkruaj arsyen e pushimit (opcionale)…"
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    {...register('Description')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Balance sidebar ── */}
        <Grid item xs={12} md={5}>
          <Stack spacing={1.5}>
            {/* Kalendari link card */}
            <Card
              component={Link}
              to="/my-leaves"
              elevation={0}
              sx={{
                border: '1px solid', borderColor: alpha('#3E8FA3', 0.4), borderRadius: 2,
                textDecoration: 'none', display: 'block',
                background: 'linear-gradient(135deg, #f0f9fb 0%, #e6f4f7 100%)',
                transition: 'all .15s',
                '&:hover': { borderColor: '#3E8FA3', boxShadow: '0 2px 10px rgba(62,143,163,0.2)', transform: 'translateY(-1px)' },
              }}
            >
              <CardContent sx={{ p: '10px 14px !important', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
                  background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CalendarMonthIcon sx={{ color: '#fff', fontSize: 16 }} />
                </Box>
                <Typography variant="body2" fontWeight={700} color="#256878" flex={1}>
                  Kalendari
                </Typography>
                <ArrowBackIcon sx={{ fontSize: 14, color: '#3E8FA3', transform: 'rotate(180deg)' }} />
              </CardContent>
            </Card>

            {/* Balance card */}
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={0.75} alignItems="center" mb={1.5}>
                  <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                    Bilanci i Pushimeve {new Date().getFullYear()}
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  {Object.entries(POLICY).map(([id, pol]) => {
                    const usage = currentYearUsage();
                    const used = usage[Number(id)] || 0;
                    return (
                      <BalanceBar
                        key={id}
                        label={pol.name}
                        used={used}
                        allowance={pol.allowance}
                        color={leaveTypeColor(Number(id)) || '#64748B'}
                      />
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} display="block" mb={1}>
                  Llojet e Pushimeve
                </Typography>
                <Stack spacing={0.75}>
                  {HOLIDAY_TYPES.map(t => (
                    <Stack key={t.id} direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: leaveTypeColor(t.id) || '#64748B', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" flex={1}>{t.name}</Typography>
                      <Typography variant="caption" fontWeight={600} color="text.primary">
                        {isFinite(POLICY[t.id]?.allowance) ? `${POLICY[t.id].allowance} ditë/vit` : 'Pa kufi'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
